import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './Card';
import { ScannedBusinessCard } from '../lib/api-scanned-cards';
import { Building2, Mail, Phone, User, GripVertical, MoreVertical } from 'lucide-react';

export interface PipelineStage {
  id: string;
  name: string;
  color: string;
  count: number;
}

interface PipelineBoardProps {
  cards: ScannedBusinessCard[];
  onStageChange?: (cardId: string, newStage: string) => void;
  onCardClick?: (card: ScannedBusinessCard) => void;
}

const defaultStages: PipelineStage[] = [
  { id: 'new', name: 'New Leads', color: 'bg-blue-100 border-blue-300', count: 0 },
  { id: 'contacted', name: 'Contacted', color: 'bg-purple-100 border-purple-300', count: 0 },
  { id: 'meeting', name: 'Meeting Scheduled', color: 'bg-amber-100 border-amber-300', count: 0 },
  { id: 'proposal', name: 'Proposal Sent', color: 'bg-orange-100 border-orange-300', count: 0 },
  { id: 'won', name: 'Won', color: 'bg-emerald-100 border-emerald-300', count: 0 },
  { id: 'lost', name: 'Lost', color: 'bg-red-100 border-red-300', count: 0 },
];

export function SalesPipelineBoard({ cards, onStageChange, onCardClick }: PipelineBoardProps) {
  const [stages, setStages] = useState<PipelineStage[]>(defaultStages);
  const [draggedCard, setDraggedCard] = useState<ScannedBusinessCard | null>(null);

  // Group cards by stage (using syncStatus as a proxy for pipeline stage)
  const groupedCards: Record<string, ScannedBusinessCard[]> = {
    new: cards.filter(c => c.processingStatus === 'COMPLETED' && c.syncStatus === 'UNSYNCED'),
    contacted: cards.filter(c => c.syncStatus === 'SYNCED' && !c.leadId),
    meeting: cards.filter(c => c.leadId && !c.crmPersonId),
    proposal: cards.filter(c => c.crmPersonId && c.syncStatus === 'SYNCED'),
    won: cards.filter(c => c.tags?.some(t => t.tag === 'won')),
    lost: cards.filter(c => c.tags?.some(t => t.tag === 'lost')),
  };

  // Update stage counts
  const updatedStages = stages.map(stage => ({
    ...stage,
    count: groupedCards[stage.id]?.length || 0
  }));

  function handleDragStart(card: ScannedBusinessCard) {
    setDraggedCard(card);
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault();
  }

  function handleDrop(stageId: string) {
    if (draggedCard && onStageChange) {
      onStageChange(draggedCard.id, stageId);
    }
    setDraggedCard(null);
  }

  return (
    <div className="flex gap-4 overflow-x-auto pb-4">
      {updatedStages.map((stage) => (
        <div
          key={stage.id}
          className={`flex-shrink-0 w-80 rounded-xl border-2 ${stage.color} bg-white`}
          onDragOver={handleDragOver}
          onDrop={() => handleDrop(stage.id)}
        >
          {/* Stage Header */}
          <div className="p-4 border-b border-current/20">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-slate-900">{stage.name}</h3>
              <span className="px-2 py-1 bg-white/60 rounded-full text-sm font-medium">
                {stage.count}
              </span>
            </div>
          </div>

          {/* Cards in Stage */}
          <div className="p-3 space-y-3 min-h-[200px] max-h-[calc(100vh-300px)] overflow-y-auto">
            {groupedCards[stage.id]?.map((card) => (
              <div
                key={card.id}
                draggable
                onDragStart={() => handleDragStart(card)}
                onClick={() => onCardClick?.(card)}
                className="bg-white rounded-lg border border-slate-200 p-4 shadow-sm cursor-move hover:shadow-md transition-shadow"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <GripVertical className="w-4 h-4 text-slate-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-slate-900 truncate">
                        {card.fullName || [card.firstName, card.lastName].filter(Boolean).join(' ') || 'Unknown'}
                      </h4>
                    </div>
                    {card.title && (
                      <p className="text-sm text-slate-500 truncate">{card.title}</p>
                    )}
                    {card.company && (
                      <div className="flex items-center gap-1 mt-2 text-sm text-slate-600">
                        <Building2 className="w-3 h-3" />
                        <span className="truncate">{card.company}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                      {card.email && (
                        <div className="flex items-center gap-1 truncate">
                          <Mail className="w-3 h-3" />
                          <span className="truncate">{card.email}</span>
                        </div>
                      )}
                      {card.phone && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          <span>{card.phone}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <button className="flex-shrink-0 p-1 hover:bg-slate-100 rounded">
                    <MoreVertical className="w-4 h-4 text-slate-400" />
                  </button>
                </div>

                {/* Card Tags */}
                {card.tags && card.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {card.tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag.id}
                        className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full"
                      >
                        {tag.tag}
                      </span>
                    ))}
                    {card.tags.length > 2 && (
                      <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
                        +{card.tags.length - 2}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))}

            {groupedCards[stage.id]?.length === 0 && (
              <div className="text-center py-8 text-slate-400">
                <User className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No cards in this stage</p>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
